import { useContext, useMemo } from 'react'
import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AccountGroupKey } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { WelcomeTradingAccountKey } from 'api/Trading-Accounts/Types/WelcomeTradingAccountDto'
import { Link } from 'components/Link'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

import '../TradingAccount.scss'

import { TradingAccountKey } from 'api/Clients/Types/TradingAccount'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const NameCell: React.FC<GridCellProps> = (props) => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const canViewPaymentAgent = useMemo(() => {
    return hasPermission(permissions, PermissionsEnum.CanViewDemoAccounts)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (canViewPaymentAgent) {
    return (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: generatePath(links.recordsDemoAccounts.detailsUrl as string, {
              id: props.dataItem.id,
              slug: 'details',
            }),
          }}
        >
          {props.dataItem.name}
        </Link>
      </td>
    )
  }

  return <td>{props.dataItem.name}</td>
}

export const demoTradingAccountColumns: GridColumn[] = [
  {
    title: 'Name',
    field: WelcomeTradingAccountKey.name,
    cell: NameCell,
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Lead',
    field: WelcomeTradingAccountKey.lead,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.lead?.id) {
        return <td />
      }
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsLeads.detailsUrl as string, {
                id: props.dataItem.lead.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.lead.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Account Group',
    field: WelcomeTradingAccountKey.accountGroup,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.accountGroup?.id) {
        return <td />
      }
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsAccountGroups.detailsUrl as string, {
                id: props.dataItem.accountGroup.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.accountGroup.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
  },
  // TODO: temporary is hidden (backend in progress)
  // {
  //     title: 'Account Type',
  //     field: WelcomeTradingAccountKey.accountType,
  //     filter: 'text',
  //     show: true,
  //     className: 'trading-account-table-columns__content',
  // },
  {
    title: 'Platform Type',
    field: WelcomeTradingAccountKey.platformType,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Status',
    field: WelcomeTradingAccountKey.status,
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'TV',
    field: TradingAccountKey.isTradingViewConnected,
    cell: (props: GridCellProps): JSX.Element => {
      const isTradingViewConnected = props.dataItem.isTradingViewConnected
      if (isTradingViewConnected === null) {
        return <td style={{ textAlign: 'center' }}>N/A</td>
      }

      return (
        <td style={{ textAlign: 'center' }}>
          <input type='checkbox' checked={isTradingViewConnected} disabled />
        </td>
      )
    },
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Country',
    field: AccountGroupKey.COUNTRY,
    show: true,
    filter: 'text',
  },
  {
    title: 'Entity',
    field: WelcomeTradingAccountKey.company,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.tickmillCompany?.id) {
        return <td />
      }

      return (
        <td>
          <CompanyTag id={props.dataItem.tickmillCompany.id}>
            {props.dataItem.tickmillCompany.name}
          </CompanyTag>
        </td>
      )
    },
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: WelcomeTradingAccountKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className='transaction-columns__date'>
        {formatLocalDateUTC(props.dataItem.created, true)}
      </td>
    ),
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Date Updated',
    field: WelcomeTradingAccountKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className='transaction-columns__date'>
        {formatLocalDateUTC(props.dataItem.updated, true)}
      </td>
    ),
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
  },
]
